import { Button, Card } from '@ntconsult/aluma-react';
import { AlertTriangle } from 'lucide-react';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type { Campanha } from '../types/Campanha';
import type { Progresso } from '../types/Progresso';
import { EscritasAxiisSecao } from './EscritasAxiisSecao';
import { buscaDoFunil, FILTRO_FUNIL, type NumeroFunil, porcentagem } from './funil';
import { ParticipantesAba } from './ParticipantesAba';
import { RodadaConvites } from './RodadaConvites';
import { SincronizacaoStatus } from './SincronizacaoStatus';

const NUMEROS = ['naCampanha', 'convidados', 'acessaram', 'responderam'] as const;

/** Quem ficou para trás em cada passo (R4), com o valor de cada botão. */
function abaixoDe(n: (typeof NUMEROS)[number], p: Progresso): [NumeroFunil, number][] {
  if (n === 'convidados')
    return [
      ['naoConvidados', p.naoConvidados],
      ['falhas', p.falhasConvite],
    ];
  if (n === 'acessaram') return [['naoAcessaram', p.naCampanha - p.acessaram]];
  if (n === 'responderam') return [['pararamNoMeio', p.acessaramSemResponder]];
  return [];
}

/**
 * Painel da execução (spec 29 R1 a R7, spec 34 R13 e R14, spec 36 R6): o funil em quatro cartões iguais, com tudo
 * dentro deles; cada número filtra a lista logo abaixo, sem sair da página. Depois, o último
 * envio, a gravação no Axiis e a lista. Sem setas: "Acessaram" pode passar de "Convidados" (R1).
 */
export function PainelAcompanhamento({
  campanha,
  progresso,
}: {
  campanha: Campanha;
  progresso: Progresso;
}) {
  const { t } = useTranslation('campanhas');
  const navigate = useNavigate();
  const lista = useRef<HTMLDivElement>(null);
  // A lista está na mesma página: troca os filtros e rola até ela (R14).
  const ir = (k: NumeroFunil) => {
    navigate({ search: buscaDoFunil(FILTRO_FUNIL[k]) });
    lista.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  const vazia = progresso.naCampanha === 0;

  return (
    <div className="flex flex-col gap-5">
      <section className="grid grid-cols-2 gap-4 sm:grid-cols-4" data-funil>
        {NUMEROS.map((n) => {
          // Número e rótulo na mesma linha: o funil é um resumo, a lista embaixo é o principal.
          const conteudo = (
            <span className="flex flex-wrap items-baseline gap-x-2">
              <span className="text-2xl font-semibold leading-tight text-foreground">
                {progresso[n]}
              </span>
              <span className="text-sm text-foreground-muted">{t(`funil.${n}`)}</span>
              {n === 'responderam' && !vazia && (
                <span className="text-sm text-foreground-muted" data-porcentagem>
                  {t('funil.doTotal', {
                    p: porcentagem(progresso.responderam, progresso.naCampanha),
                  })}
                </span>
              )}
            </span>
          );
          return (
            <Card
              key={n}
              className="relative flex h-full min-w-0 flex-col p-1.5 pr-10"
              data-numero={n}
            >
              {/* Sem ninguém, só "Na campanha 0" leva a algum lugar (R5). */}
              {vazia && n !== 'naCampanha' ? (
                <p className="px-3 py-1.5">{conteudo}</p>
              ) : (
                <Button
                  variant="ghost"
                  className="h-auto w-full flex-col items-start justify-start gap-0 whitespace-normal px-3 py-1.5 text-left"
                  onClick={() => ir(n)}
                >
                  {conteudo}
                </Button>
              )}
              {/* Segunda linha sempre reservada: o que ficou para trás, na mesma posição em todos. */}
              <div className="flex min-h-8 flex-wrap items-center">
                {abaixoDe(n, progresso)
                  .filter(([, v]) => v >= 1)
                  .map(([k, v]) => (
                    <Button
                      key={k}
                      variant="ghost"
                      size="sm"
                      className="h-auto min-h-8 justify-start whitespace-normal py-1 text-left"
                      leftIcon={
                        k === 'falhas' ? (
                          <AlertTriangle aria-hidden className="text-warning" />
                        ) : undefined
                      }
                      onClick={() => ir(k)}
                    >
                      {t(`funil.${k}`, { count: v })}
                    </Button>
                  ))}
              </div>
              {/* Detalhe do último envio e da gravação no Axiis, no cartão a que se referem. */}
              {n === 'convidados' && (
                <span className="absolute right-1.5 top-1.5">
                  <RodadaConvites campanhaId={campanha.id} modo="info" />
                </span>
              )}
              {n === 'responderam' && progresso.responderam >= 1 && (
                <span className="absolute right-1.5 top-1.5">
                  <EscritasAxiisSecao campanha={campanha} />
                </span>
              )}
            </Card>
          );
        })}
      </section>

      {/* Só enquanto envia ou se o envio parou: o concluído fica no ícone de "Convidados". */}
      <RodadaConvites campanhaId={campanha.id} onFalhas={() => ir('falhas')} modo="faixa" />
      <div ref={lista} className="flex scroll-mt-4 flex-col gap-4" data-lista>
        <SincronizacaoStatus campanhaId={campanha.id} naCampanha={progresso.naCampanha} />
        {/* Os cartões de totais repetiriam o funil: aqui só os números de cima filtram. */}
        <ParticipantesAba campanhaId={campanha.id} fase={progresso.fase} comTotais={false} />
      </div>
    </div>
  );
}
